import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { useUpdateGeoSchemeMutation } from '@shared/api';
import {
  fmtNumber,
  hasDeveloper,
  layoutProblems,
  sheetSize,
  workTypeShort,
  ZONE_KEYS,
  zoneRect,
  type GeoScheme,
  type GeoSchemePatchBody,
  type GeoSettings,
} from '@entities/geodesy';
import { riseIn } from '@shared/theme/animations';
import { Button } from '@shared/ui/Button';
import { IconCheck } from '@shared/ui/icons';
import { ErrorText, Eyebrow, Hint, Muted, SrOnly, useAction } from '@widgets/GeoSchemes';

/* ── Контекст мастера ──────────────────────────────────────────────────────── */

export interface WizardContextValue {
  scheme: GeoScheme;
  /** Нет права менять схему (`permissions.can_edit = false`). */
  readOnly: boolean;
  step: number;
  /** Перейти к шагу; `anchor` — id блока, к которому прокрутить; `force` — без
   *  вопроса о несохранённых правках (их только что сохранили). */
  goTo: (step: number, anchor?: string, force?: boolean) => void;
  /** Реестр несохранённых правок по ключу (у шага может быть несколько форм). */
  setDirty: (key: string, dirty: boolean) => void;
}

const WizardContext = createContext<WizardContextValue | null>(null);

export const WizardProvider = WizardContext.Provider;

export function useWizard(): WizardContextValue {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard: нет WizardProvider');
  return ctx;
}

/** Сообщить мастеру о несохранённых правках формы (снимается при размонтировании). */
export function useStepDirty(key: string, dirty: boolean): void {
  const { setDirty } = useWizard();
  useEffect(() => {
    setDirty(key, dirty);
  }, [key, dirty, setDirty]);
  useEffect(() => () => setDirty(key, false), [key, setDirty]);
}

/**
 * Сохранение настроек ИС: `PATCH settings` ЦЕЛИКОМ — свежие серверные настройки
 * с подменённым срезом шага. Так шаги не затирают правки друг друга.
 */
export function useSettingsSaver() {
  const { scheme } = useWizard();
  const [update] = useUpdateGeoSchemeMutation();
  const action = useAction();
  const save = (
    patch: (current: GeoSettings) => GeoSettings,
    extra: Omit<GeoSchemePatchBody, 'settings' | 'base_settings'> = {},
  ) =>
    action.run(
      () =>
        update({
          id: scheme.id,
          ...extra,
          settings: patch(scheme.settings),
          base_settings: scheme.settings,
        }).unwrap(),
      'Изменения не сохранены.',
    );
  return { save, busy: action.busy, error: action.error, setError: action.setError };
}

/* ── Шаги (регламент камеральщика, ТЗ §1.1) ────────────────────────────────── */

export const STEPS: ReadonlyArray<{ n: number; title: string }> = [
  { n: 1, title: 'Проектный лист' },
  { n: 2, title: 'Система координат и высоты' },
  { n: 3, title: 'Масштаб' },
  { n: 4, title: 'Черновик по ГОСТ' },
  { n: 5, title: 'Области рамки' },
  { n: 6, title: 'Съёмка и проектные данные' },
  { n: 7, title: 'Тип работ и отклонения' },
  { n: 8, title: 'Чертёж' },
  { n: 9, title: 'Корректировки' },
];

export type StepState = 'done' | 'todo' | 'warn' | 'error' | 'optional';

export interface StepStatus {
  state: StepState;
  /** Короткая выжимка данных шага под заголовком в степпере. */
  note: string;
}

const STATE_TEXT: Readonly<Record<StepState, string>> = {
  done: 'выполнен',
  todo: 'не выполнен',
  warn: 'есть замечания',
  error: 'требует исправления',
  optional: 'по необходимости',
};

/** Отметки выполнения шагов — по данным схемы, без локальных флагов. */
export function computeStepStatuses(s: GeoScheme): Record<number, StepStatus> {
  const st = s.settings;
  const b = st.binding;
  const nEl = s.design.elements.length;
  const nPt = s.survey.points.length;

  const s1: StepStatus = s.sheet
    ? {
        state: 'done',
        note: `${s.sheet.kind.toUpperCase()}${s.sheet.pages > 1 ? `, стр. ${s.sheet.page + 1}` : ''}`,
      }
    : b.mode === 'angle' || nEl > 0
      ? { state: 'optional', note: 'без подложки' }
      : { state: 'todo', note: 'лист не загружен' };

  const crsOk = st.crs.name.trim() !== '' && st.crs.height_system.trim() !== '';
  let s2: StepStatus;
  if (!crsOk) s2 = { state: 'todo', note: 'СК или высоты не указаны' };
  else if (b.mode === 'sheet') {
    s2 = b.result
      ? {
          state: b.result.rms_m > 0.05 ? 'warn' : 'done',
          note: `${st.crs.name}, RMS ${fmtNumber(b.result.rms_m * 1000, 1)} мм`,
        }
      : { state: 'todo', note: 'лист не привязан' };
  } else if (b.mode === 'none' && s.sheet) {
    s2 = { state: 'warn', note: 'подложка без привязки' };
  } else s2 = { state: 'done', note: st.crs.name };

  const rd = st.scale.design_denominator ?? s.sheet?.scale_hint ?? null;
  const den = st.scale.denominator;
  const s3: StepStatus =
    den == null
      ? { state: 'optional', note: 'авто' }
      : rd != null && den > rd
        ? { state: 'error', note: `1:${den} мельче РД` }
        : { state: 'done', note: `1:${den}` };

  const s4: StepStatus =
    st.stamp.designation.trim() !== '' && hasDeveloper(st.stamp.people)
      ? {
          state: 'done',
          note: `${st.sheet.format}, ${st.sheet.orientation === 'landscape' ? 'альбомный' : 'книжный'}`,
        }
      : { state: 'todo', note: 'штамп не заполнен' };

  const problems = layoutProblems(
    st.layout.zones,
    sheetSize(st.sheet.format, st.sheet.orientation),
  );
  const zoneCount = ZONE_KEYS.filter((k) => zoneRect(st.layout.zones, k) != null).length;
  const s5: StepStatus =
    problems.length > 0
      ? { state: 'error', note: `пересечений: ${problems.length}` }
      : { state: 'done', note: `областей: ${zoneCount}` };

  const s6: StepStatus = s.design_stale
    ? { state: 'error', note: 'проект с прежнего листа' }
    : nEl > 0 && nPt > 0
      ? { state: 'done', note: `${nEl} эл. · ${nPt} точ.` }
      : {
          state: 'todo',
          note: nEl === 0 && nPt === 0 ? 'нет данных' : nEl === 0 ? 'нет РД' : 'нет съёмки',
        };

  const dev = s.deviations;
  const s7: StepStatus = !dev
    ? { state: 'todo', note: `${workTypeShort(s.work_type)}, не рассчитано` }
    : dev.totals.exceeded > 0
      ? {
          state: st.agreement ? 'done' : 'warn',
          note: `${workTypeShort(s.work_type)}, превышений: ${dev.totals.exceeded}`,
        }
      : { state: 'done', note: `${workTypeShort(s.work_type)}, всё в допуске` };

  const hasErrors = s.checks.some((c) => c.level === 'error');
  const s8: StepStatus =
    s.versions.length === 0
      ? { state: 'todo', note: 'не сформирована' }
      : { state: hasErrors ? 'warn' : 'done', note: `версия ${s.current_version}` };

  const corrected =
    s.versions.some((v) => v.source === 'uploaded') || (dev?.overrides.length ?? 0) > 0;
  const s9: StepStatus =
    s.status === 'rejected'
      ? { state: 'error', note: 'нужна корректировка' }
      : corrected
        ? { state: 'done', note: 'есть правки' }
        : { state: 'optional', note: 'при необходимости' };

  return { 1: s1, 2: s2, 3: s3, 4: s4, 5: s5, 6: s6, 7: s7, 8: s8, 9: s9 };
}

/** С какого шага открывать карточку, если шаг не задан в URL. */
export function defaultStep(s: GeoScheme): number {
  if (s.status === 'rejected') return 9;
  if (s.status !== 'draft') return 8;
  const statuses = computeStepStatuses(s);
  const first = STEPS.find((x) => {
    const state = statuses[x.n]?.state;
    return x.n <= 8 && (state === 'todo' || state === 'error');
  });
  return first?.n ?? 8;
}

/* ── Степпер: геодезический ход — станции, соединённые пунктиром ──────────── */

const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;

  @container geo-wizard (max-width: 860px) {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(1)};
    overflow-x: auto;
    padding-bottom: ${({ theme }) => theme.spacing(2)};
  }
`;

const StepItem = styled.li`
  position: relative;

  /* Пунктир хода между станциями. */
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 25px;
    top: 40px;
    bottom: -6px;
    border-left: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  }

  @container geo-wizard (max-width: 860px) {
    flex: 0 0 auto;
    &:not(:last-child)::before {
      display: none;
    }
  }
`;

const StepButton = styled.button<{ $current: boolean }>`
  display: grid;
  grid-template-columns: 28px 1fr;
  align-items: start;
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $current }) => ($current ? theme.colors.backgroundElevated : 'transparent')};
  border-color: ${({ theme, $current }) => ($current ? theme.colors.border : 'transparent')};
  box-shadow: ${({ theme, $current }) => ($current ? theme.shadow.sm : 'none')};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }

  @container geo-wizard (max-width: 860px) {
    grid-template-columns: 28px auto;
    align-items: center;
    width: auto;
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2.5)}`};
  }
`;

const Station = styled.span<{ $state: StepState; $current: boolean }>`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.background};
  border: 1.5px solid ${({ theme }) => theme.colors.borderStrong};
  color: ${({ theme }) => theme.colors.text.secondary};

  ${({ theme, $state }) => {
    switch ($state) {
      case 'done':
        return css`
          border-color: ${theme.colors.status.success};
          color: ${theme.colors.status.success};
          background: ${theme.colors.status.success}14;
        `;
      case 'warn':
        return css`
          border-color: ${theme.colors.status.warning};
          color: ${theme.colors.status.warning};
        `;
      case 'error':
        return css`
          border-color: ${theme.colors.status.danger};
          color: ${theme.colors.status.danger};
          background: ${theme.colors.status.danger}14;
        `;
      case 'optional':
        return css`
          border-style: dotted;
        `;
      default:
        return css`
          border-style: dashed;
        `;
    }
  }}

  ${({ theme, $current }) =>
    $current &&
    css`
      border-color: ${theme.colors.primary};
      background: ${theme.colors.primary};
      color: ${theme.colors.backgroundElevated};
      box-shadow: 0 0 0 4px ${theme.colors.primarySoft};
    `}
`;

const StepText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const StepTitle = styled.span<{ $current: boolean }>`
  font-size: 13.5px;
  font-weight: ${({ $current }) => ($current ? 600 : 500)};
  line-height: 1.3;
  color: ${({ theme, $current }) => ($current ? theme.colors.text.primary : theme.colors.text.secondary)};

  @container geo-wizard (max-width: 860px) {
    white-space: nowrap;
  }
`;

const StepNote = styled.span<{ $state: StepState }>`
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.3;
  overflow-wrap: anywhere;
  color: ${({ theme, $state }) =>
    $state === 'error' ? theme.colors.status.danger : theme.colors.text.secondary};

  @container geo-wizard (max-width: 860px) {
    display: none;
  }
`;

export function Stepper({
  current,
  statuses,
  onSelect,
}: {
  current: number;
  statuses: Record<number, StepStatus>;
  onSelect: (n: number) => void;
}) {
  const listRef = useRef<HTMLOListElement>(null);

  // В узкой раскладке степпер — горизонтальная лента: подкручиваем её к текущему
  // шагу. Только scrollLeft ленты — страница по вертикали не дёргается.
  useEffect(() => {
    const list = listRef.current;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const item = list.querySelector('[aria-current="step"]');
    if (!item) return;
    const l = list.getBoundingClientRect();
    const r = item.getBoundingClientRect();
    list.scrollLeft += r.left - l.left - (l.width - r.width) / 2;
  }, [current]);

  return (
    <nav aria-label="Шаги исполнительной схемы">
      <StepList ref={listRef}>
        {STEPS.map((s) => {
          const status: StepStatus = statuses[s.n] ?? { state: 'todo', note: '' };
          const isCurrent = s.n === current;
          return (
            <StepItem key={s.n}>
              <StepButton
                type="button"
                $current={isCurrent}
                aria-current={isCurrent ? 'step' : undefined}
                onClick={() => onSelect(s.n)}
              >
                <Station $state={status.state} $current={isCurrent} aria-hidden>
                  {status.state === 'done' && !isCurrent ? <IconCheck size={14} /> : s.n}
                </Station>
                <StepText>
                  <StepTitle $current={isCurrent}>{s.title}</StepTitle>
                  <StepNote $state={status.state}>{status.note}</StepNote>
                  <SrOnly>
                    Шаг {s.n}: {STATE_TEXT[status.state]}
                  </SrOnly>
                </StepText>
              </StepButton>
            </StepItem>
          );
        })}
      </StepList>
    </nav>
  );
}

/* ── Оболочка шага: заголовок, тело, панель сохранения, навигация ─────────── */

const Shell = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  min-width: 0;
  animation: ${riseIn} 220ms ${({ theme }) => theme.motion.easeOut};
`;

const Head = styled.header`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const StepHeading = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.015em;
  text-wrap: balance;
`;

const SaveBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: ${({ theme }) => theme.z.sticky};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  box-shadow: ${({ theme }) => theme.shadow.md};
`;

const SaveText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13.5px;
  font-weight: 600;
`;

const Nav = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding-top: ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

export interface StepSaveProps {
  dirty: boolean;
  busy: boolean;
  error: string | null;
  onSave: () => Promise<boolean>;
  onReset: () => void;
  /** Почему сохранить нельзя (пересечения зон, неполные точки). */
  blockedReason?: string | null;
}

export function StepShell({
  n,
  lead,
  save,
  children,
}: {
  n: number;
  lead?: ReactNode;
  save?: StepSaveProps;
  children: ReactNode;
}) {
  const { readOnly, goTo } = useWizard();
  const step = STEPS.find((s) => s.n === n);
  const prev = STEPS.find((s) => s.n === n - 1);
  const next = STEPS.find((s) => s.n === n + 1);
  const dirty = Boolean(save?.dirty) && !readOnly;
  const blocked = Boolean(save?.blockedReason);

  async function goNext() {
    if (!next) return;
    if (dirty && save) {
      if (blocked) return;
      const ok = await save.onSave();
      if (!ok) return;
      // Только что сохранили — флаг правок в замыкании ещё старый, не спрашиваем.
      goTo(next.n, undefined, true);
      return;
    }
    goTo(next.n);
  }

  return (
    <Shell aria-labelledby={`geo-step-${n}`}>
      <Head>
        <Eyebrow>
          Шаг {String(n).padStart(2, '0')} из {STEPS.length}
        </Eyebrow>
        <StepHeading id={`geo-step-${n}`}>{step?.title}</StepHeading>
        {lead && <Hint>{lead}</Hint>}
      </Head>

      {children}

      {dirty && save && (
        <SaveBar role="region" aria-label="Несохранённые изменения шага">
          <SaveText>
            Есть несохранённые изменения
            {save.blockedReason && <Muted>{save.blockedReason}</Muted>}
            {save.error && <ErrorText role="alert">{save.error}</ErrorText>}
          </SaveText>
          <div>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={save.onReset}
              disabled={save.busy}
            >
              Отменить
            </Button>{' '}
            <Button
              type="button"
              $variant="primary"
              $size="sm"
              onClick={() => void save.onSave()}
              disabled={save.busy || blocked}
            >
              {save.busy ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </div>
        </SaveBar>
      )}

      <Nav>
        {prev ? (
          <Button type="button" $variant="ghost" onClick={() => goTo(prev.n)}>
            ← {prev.title}
          </Button>
        ) : (
          <span />
        )}
        {next && (
          <Button
            type="button"
            $variant="secondary"
            onClick={() => void goNext()}
            disabled={dirty && (blocked || Boolean(save?.busy))}
          >
            {dirty ? 'Сохранить и далее' : next.title} →
          </Button>
        )}
      </Nav>
    </Shell>
  );
}
